import { useEffect, useRef, useCallback } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { ChatMessage } from './ChatMessage';
import type { ChatMessage as ChatMessageType } from '@/types/chat';
import { Loader2, AlertCircle, Sparkles } from 'lucide-react';

interface ChatMessagesProps {
  messages: ChatMessageType[];
  isLoading?: boolean;
  isSending?: boolean;
  sendError?: Error | null;
  onRetry?: () => void;
}

export function ChatMessages({
  messages,
  isLoading,
  isSending,
  sendError,
  onRetry,
}: ChatMessagesProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const prevMessageCountRef = useRef(0);

  // Auto-scroll only when new messages are added (not on initial load of history)
  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (el) {
      // Use scrollTop instead of scrollIntoView for iOS Safari compatibility
      const scrollParent = el.parentElement;
      if (scrollParent) {
        scrollParent.scrollTop = scrollParent.scrollHeight;
      }
    }
  }, []);

  useEffect(() => {
    if (messages.length > prevMessageCountRef.current) {
      scrollToBottom();
    }
    prevMessageCountRef.current = messages.length;
  }, [messages.length, scrollToBottom]);

  // Also scroll when sending starts (to show the thinking indicator)
  useEffect(() => {
    if (isSending) {
      scrollToBottom();
    }
  }, [isSending, scrollToBottom]);

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (messages.length === 0 && !isSending) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center px-4">
        <div className="rounded-full bg-muted p-4">
          <Sparkles className="h-8 w-8 text-muted-foreground" />
        </div>
        <div>
          <h3 className="font-semibold">Ask Poco anything</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Poco has context about this client's meetings, projects, goals, and more.
          </p>
        </div>
      </div>
    );
  }

  return (
    <ScrollArea className="flex-1">
      <div className="flex flex-col">
        {messages.map((message) => (
          <ChatMessage
            key={message.id}
            message={message}
            isOptimistic={message.id.startsWith('temp-')}
          />
        ))}

        {isSending && (
          <div className="flex gap-3 p-4">
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-primary animate-pulse" />
            </div>
            <div className="bg-muted rounded-lg px-4 py-3">
              <p className="text-sm text-muted-foreground mb-1">Poco is thinking...</p>
              <div className="flex gap-1">
                <span
                  className="h-2 w-2 rounded-full bg-muted-foreground/50 animate-bounce"
                  style={{ animationDelay: '0ms' }}
                />
                <span
                  className="h-2 w-2 rounded-full bg-muted-foreground/50 animate-bounce"
                  style={{ animationDelay: '150ms' }}
                />
                <span
                  className="h-2 w-2 rounded-full bg-muted-foreground/50 animate-bounce"
                  style={{ animationDelay: '300ms' }}
                />
              </div>
            </div>
          </div>
        )}

        {sendError && !isSending && (
          <div className="flex gap-3 p-4">
            <div className="h-8 w-8 rounded-full bg-destructive/10 flex items-center justify-center shrink-0">
              <AlertCircle className="h-4 w-4 text-destructive" />
            </div>
            <div className="bg-destructive/5 border border-destructive/20 rounded-lg px-4 py-3">
              <p className="text-sm text-destructive font-medium">Failed to send message</p>
              <p className="text-xs text-muted-foreground mt-1">
                {sendError.message || 'Something went wrong. Please try again.'}
              </p>
              {onRetry && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2 h-7 text-xs"
                  onClick={onRetry}
                >
                  Try again
                </Button>
              )}
            </div>
          </div>
        )}

        <div ref={scrollRef} />
      </div>
    </ScrollArea>
  );
}
